import { NavigatorName } from "../../../enums/command-hub/navigator";
import i18n from "../../../i18n";
import { MissionColor } from "../../../utils/mission-color";
import RswNavigator, { getNavigatorDescription } from "../navigator";
import { ScheduleEntryApi } from "../scheduling/schedule-entry";

interface Mission {
  id: string;
  name: string;
  room: string | null;
  mode: string;
  selectedZoneIds: string[];
  selectedLandmarkIds: string[];
  fixedOrder: boolean;
  doWallFollow: boolean;
  repeat: number;
  color: MissionColor;
}

export interface MissionApiData {
  id: string;
  name: string;
  room: string | null;
  mode: string;
  selectedZoneIds: string[];
  selectedLandmarkIds: string[];
  fixedOrder: boolean;
  doWallFollow: boolean;
  repeat: number;
  color: MissionColor;
  schedules: ScheduleEntryApi[];
}

export const mapMissionApiDataToMission = (apiData: MissionApiData) => {
  let { schedules: _, ...rest } = apiData;
  return {
    ...rest,
  } as Mission;
};

export const mapMissionToApi = (mission: Mission) =>
  ({
    ...mission,
  } as MissionApiData);

export const mapPartialMissionToApi = (mission: Partial<Mission>) =>
  ({
    ...mission,
  } as Partial<MissionApiData>);

export default Mission;

export const copyMission = (mission: Mission) => {
  return {
    ...mission,
    selectedLandmarkIds: [...mission.selectedLandmarkIds],
    selectedZoneIds: [...mission.selectedZoneIds],
  } as Mission;
};

export const getDefaultMissionName = (
  navigator: RswNavigator | string,
  roomMissions: Mission[],
  repeat: number,
  missionId?: string
) => {
  const missionAlreadyExists = roomMissions.find((m) => m.id === missionId);
  let currentMode = "";
  let currentMissionNumber = 0;

  if (missionAlreadyExists) {
    // extrapolate mode and mission number in order to get the correct number
    currentMode = i18n.t("rooms-cleaning");
    if (missionAlreadyExists.mode === NavigatorName.ZoneCleaning) {
      currentMode = i18n.t("zone-cleaning");
    } else if (missionAlreadyExists.mode === NavigatorName.Maintenance) {
      currentMode = i18n.t("maintenance-cleaning");
    }

    const missionNameMatch = missionAlreadyExists.name.match(/\d+/);
    currentMissionNumber = missionNameMatch ? parseInt(missionNameMatch[0], 10) : 1;
  }

  let newMode = i18n.t("rooms-cleaning");
  if (navigator === NavigatorName.ZoneCleaning) {
    newMode = i18n.t("zone-cleaning");
  } else if (navigator === NavigatorName.Maintenance) {
    newMode = i18n.t("maintenance-cleaning");
  }

  let missionsNumber = roomMissions.length;
  if (missionAlreadyExists && currentMode === newMode) {
    // mode stays the same, don't update mission number
    missionsNumber = currentMissionNumber;
  } else {
    if (navigator !== NavigatorName.ZoneCleaning && navigator !== NavigatorName.Maintenance) {
      missionsNumber =
        roomMissions.filter((m) => m.mode !== NavigatorName.ZoneCleaning && m.mode !== NavigatorName.Maintenance)
          .length + 1;
    } else if (navigator === NavigatorName.ZoneCleaning) {
      missionsNumber = roomMissions.filter((m) => m.mode === NavigatorName.ZoneCleaning).length + 1;
    } else if (navigator === NavigatorName.Maintenance) {
      missionsNumber = roomMissions.filter((m) => m.mode === NavigatorName.Maintenance).length + 1;
    }
  }

  let cleaningRepeat = i18n.t("label-mission-repeat-once");
  if (repeat === 2) {
    cleaningRepeat = i18n.t("label-mission-repeat-twice");
  } else if (repeat === 3) {
    cleaningRepeat = i18n.t("label-mission-repeat-thrice");
  } else if (repeat === -1) {
    cleaningRepeat = i18n.t("label-mission-repeat-continuously");
  }

  const navigatorName = getNavigatorDescription(navigator);
  const repeatToUse = newMode === i18n.t("maintenance-cleaning") ? i18n.t("label-mission-repeat-once") : cleaningRepeat; // maintenance mode doesn't have repeat, but the name should maintain this structure

  return `${newMode} ${i18n.t("mission-name-default")} ${missionsNumber} - ${navigatorName} ${repeatToUse}`;
};

export const isDefaultMissionNameStructure = (missionName: string) => {
  const modePattern = `${i18n.t("rooms-cleaning")}|${i18n.t("zone-cleaning")}|${i18n.t("maintenance-cleaning")}`;
  const repeatPattern = `${i18n.t("label-mission-repeat-once")}|${i18n.t("label-mission-repeat-twice")}|${i18n.t(
    "label-mission-repeat-thrice",
  )}|${i18n.t("label-mission-repeat-continuously")}`;

  const regexPattern = `^(${modePattern}) ${i18n.t("mission-name-default")} (\\d+) - (.+) (${repeatPattern})$`;

  const regex = new RegExp(regexPattern);
  return regex.test(missionName);
};

export const getNextColorForMission = (numberOfPreviousMissions: number) => {
  const nextColorIndex = numberOfPreviousMissions % Object.values(MissionColor).length;
  return Object.values(MissionColor)[nextColorIndex];
};
